import { useState } from 'react'
import styles from './styles.module.css'
import { ArrowRigth, CampaingCheckIcon, CheckIcon, Eye } from '../../../assets/svgs/icons'
import { notifyErrorCustom } from '../../../utils/notify'
import { motion } from 'framer-motion'

import { ForgotPasswordService } from '../../../services'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LuCheck } from 'react-icons/lu'
import { FaCheck } from 'react-icons/fa'
import { PiEyeClosed } from 'react-icons/pi'

const PASSWORD_REQUIREMENTS = [
  {
    label: 'Mínimo de 8 caracteres',
    isMet: (pass) => pass.length >= 8,
  },
  {
    label: 'Contém letras e números',
    isMet: (pass) => {
      if (!pass) return false
      return /[A-Za-z]/.test(pass) && /\d/.test(pass)
    },
  },
  {
    label: 'Pelo menos 1 caractere especial',
    isMet: (pass) => {
      if (!pass) return false
      return /[^A-Za-z0-9]/.test(pass)
    },
  },
  {
    label: 'As senhas precisam coincidir',
    isMet: (pass, confirmPass) => pass !== '' && pass === confirmPass,
  },
]

function StepPassword({
  setPass,
  setConfirmPass,
  pass,
  confirmPass,
  email,
  code,
}) {
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showPassConfirm, setShowPassConfirm] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handlePass = (type) => {
    if (type === 'pass') {
      setShowPass(!showPass)
    } else {
      setShowPassConfirm(!showPassConfirm)
    }
  }

  const handleNavigate = async (type) => {
    if (type === 'next') {
      if (pass === '' || confirmPass === '') {
        notifyErrorCustom(t('fast_publishing_pass_enter_your_password_alert'))
        return
      }
      if (pass.length < 9 || confirmPass.length < 9) {
        notifyErrorCustom(t('fast_publishing_pass_short_password_alert'))
        return
      }
      if (pass != confirmPass) {
        notifyErrorCustom(t('fast_publishing_pass_confirmation_password_alert'))
        return
      }

      if (!/[A-Za-z]/.test(pass) || !/\d/.test(pass)) {
        notifyErrorCustom('A senha precisa conter letras e números.')
        return
      }

      if (!/[^A-Za-z0-9]/.test(pass)) {
        notifyErrorCustom('A senha precisa conter pelo menos 1 caractere especial.')
        return
      }

      const response = await ForgotPasswordService.postResetPassword({
        email,
        code,
        newPassword: pass,
      })

      if (response.responseStatus === 200) {
        setIsSuccess(true)
      } else {
        notifyErrorCustom(t('forgotPasswordStepPassword.forgot_password_error'))
      }

      setIsLoading(false)

      // console.log('response', response)
    }
  }

  const { t } = useTranslation()
  const remainingRequirements = PASSWORD_REQUIREMENTS.filter(
    (requirement) => !requirement.isMet(pass, confirmPass)
  )

  if (isSuccess) {
    return (
      <motion.div
        initial={{ x: '100vw' }}
        animate={{ x: 0 }}
        exit={{ x: '-100vw' }}
        transition={{ type: 'tween', duration: 0.5 }}
        className={styles.step_motion}
      >
        <div className={styles.success_box}>
          <div className={styles.success_badge} aria-hidden="true">
            <div className={styles.success_badge_mark}>
              <CampaingCheckIcon color="#24c55d" size="42" />
            </div>
          </div>

          <h1 className={styles.success_title}>Senha redefinida!</h1>

          <p className={styles.success_description}>
            Sua senha foi alterada com sucesso. Você já pode acessar sua conta
            com a nova senha.
          </p>

          <button
            type="button"
            className={styles.success_button}
            onClick={() => navigate('/SignIn')}
          >
            <p> Ir para o login</p>
            <ArrowRigth size={24}/>
          </button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ x: '100vw' }}
      animate={{ x: 0 }}
      exit={{ x: '-100vw' }}
      transition={{ type: 'tween', duration: 0.5 }}
      className={styles.step_motion}
    >
      <div className={styles.box_step}>
        <div className={styles.box_page}>
          <h1 className={styles.step_title}>
            Crie uma nova senha
          </h1>

          <p className={styles.step_description}>
            Sua nova senha precisa ter ao menos 8 caracteres, com letras e
            números.
          </p>

          <div className={styles.box_input_password}>
            <div className={styles.field_group}>
              <label className={styles.input_label} htmlFor="new-password">
                Nova senha
              </label>
              <div className={styles.input_shell}>
                <span className={styles.input_icon} aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="3"
                      stroke="#A8AAB8"
                      strokeWidth="1.6"
                    />
                    <path
                      d="M4 7L12 13L20 7"
                      stroke="#A8AAB8"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <input
                  id="new-password"
                  className={styles.input_password}
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••••"
                  value={pass}
                  onChange={(e) => setPass(e.currentTarget.value)}
                />
                <button
                  type="button"
                  className={styles.icon_input}
                  onClick={() => handlePass('pass')}
                  aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPass ? <PiEyeClosed /> : <Eye />}
                </button>
              </div>
            </div>

            <div className={styles.field_group}>
              <label className={styles.input_label} htmlFor="confirm-password">
                Confirmar senha
              </label>
              <div className={styles.input_shell}>
                <span className={styles.input_icon} aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="3"
                      stroke="#A8AAB8"
                      strokeWidth="1.6"
                    />
                    <path
                      d="M4 7L12 13L20 7"
                      stroke="#A8AAB8"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <input
                  id="confirm-password"
                  className={styles.input_password}
                  type={showPassConfirm ? 'text' : 'password'}
                  placeholder="••••••••••"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.currentTarget.value)}
                />
                <button
                  type="button"
                  className={styles.icon_input}
                  onClick={() => handlePass('passConfirm')}
                  aria-label={
                    showPassConfirm ? 'Ocultar senha' : 'Mostrar senha'
                  }
                >
                  {showPassConfirm ? <PiEyeClosed /> : <Eye />}
                </button>
              </div>
            </div>

            {remainingRequirements.length > 0 && (
              <div className={styles.box_information}>
                <p className={styles.box_information_title}>
                  A senha precisa conter:
                </p>
                <ul className={styles.requirements_list}>
                  {remainingRequirements.map((requirement) => (
                    <li key={requirement.label}>{requirement.label}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className={styles.box_button}>
          <button
            type="button"
            disabled={isLoading}
            data-loading={isLoading ? 'true' : 'false'}
            className={`${styles.next_button} ${isLoading ? styles.next_button_loading : ''}`}
            onClick={() => {
              setIsLoading(true)
              handleNavigate('next')
            }}
          >
            {isLoading ? (
              <span className={styles.button_loading_content}>Carregando...</span>
            ) : (
              <span className={styles.button_continue_content}>
                <span>Redefinir senha</span>
                <ArrowRigth  size={24} />
              </span>
            )}
          </button>

          <p className={styles.login_prompt}>
            Lembrou da senha?{' '}
            <button
              type="button"
              className={styles.login_link}
              onClick={() => navigate('/SignIn')}
            >
              Voltar ao login
            </button>
          </p>
        </div>
      </div>
    </motion.div>
  )
}

export default StepPassword
